/**
 * Appearance: the white chrome below is a deliberate constant. This gallery
 * renders full-bleed over the user's photos, so the thing behind the text and
 * icons is imagery, not a themed surface, in both light and dark.
 */
/**
 * ImageGallery Component (Web)
 *
 * Web-optimized image gallery using state-driven image display
 * instead of FlatList. Supports zoom via double-click / scroll wheel
 * and pan when zoomed via mouse drag.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { Colors } from '../../../../themes/colors';
import { ChevronLeftIcon, ChevronRightIcon } from '../../icons';
import { borderRadius, colors, spacing, typography } from '../../tokens';
import { ImageGalleryHeader } from './ImageGalleryHeader';
import { ImageGalleryThumbnails } from './ImageGalleryThumbnails';
import { themedStyleSheet } from '../../../../themes/themedStyleSheet';
import { IMAGE_MAX_ZOOM_SCALE } from './zoom';

const MIN_SCALE = 1;
const ZOOM_STEP = 0.5;

export interface ImageGalleryImage {
  id: string;
  url: string;
  name?: string;
}

export interface ImageGalleryProps {
  images: ImageGalleryImage[];
  initialIndex?: number;
  onBack?: () => void;
  onImageChange?: (index: number) => void;
  showThumbnails?: boolean;
  showCounter?: boolean;
  showHeader?: boolean;
  disableImageChange?: boolean;
  renderHeader?: (
    activeImage: ImageGalleryImage,
    activeIndex: number
  ) => React.ReactNode;
  testID?: string;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  initialIndex = 0,
  onBack,
  onImageChange,
  showThumbnails = true,
  showCounter = true,
  showHeader = true,
  disableImageChange = false,
  renderHeader,
  testID,
}) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const translateStart = useRef({ x: 0, y: 0 });

  const activeImage = images[activeIndex];
  const hasThumbnails =
    showThumbnails && !disableImageChange && images.length > 1;

  const resetZoom = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const goToIndex = useCallback(
    (index: number) => {
      if (index < 0 || index >= images.length) return;
      setActiveIndex(index);
      resetZoom();
      onImageChange?.(index);
    },
    [images.length, onImageChange, resetZoom]
  );

  const handlePrev = useCallback(() => {
    if (scale > 1) return;
    goToIndex(activeIndex - 1);
  }, [activeIndex, goToIndex, scale]);

  const handleNext = useCallback(() => {
    if (scale > 1) return;
    goToIndex(activeIndex + 1);
  }, [activeIndex, goToIndex, scale]);

  const handleDoubleClick = useCallback(() => {
    if (scale > 1) {
      resetZoom();
    } else {
      setScale(2);
    }
  }, [scale, resetZoom]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setScale((prev) => {
      const next = Math.max(
        MIN_SCALE,
        Math.min(IMAGE_MAX_ZOOM_SCALE, prev + delta)
      );
      if (next <= 1) {
        setTranslate({ x: 0, y: 0 });
      }
      return next;
    });
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (scale <= 1) return;
      isDragging.current = true;
      dragStart.current = { x: e.clientX, y: e.clientY };
      translateStart.current = { ...translate };
    },
    [scale, translate]
  );

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setTranslate({
      x: translateStart.current.x + dx,
      y: translateStart.current.y + dy,
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') resetZoom();
    },
    [handlePrev, handleNext, resetZoom]
  );

  const thumbnailData = useMemo(
    () =>
      images.map((img) => ({
        uri: img.url,
        key: img.id,
      })),
    [images]
  );

  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'contain' as const,
    transform: `scale(${scale}) translate(${translate.x / scale}px, ${
      translate.y / scale
    }px)`,
    transition: isDragging.current ? 'none' : 'transform 0.2s ease-out',
    cursor: scale > 1 ? 'grab' : 'zoom-in',
    userSelect: 'none' as const,
    maxHeight: 'calc(100vh - 200px)',
  };

  const header = () =>
    renderHeader && (
      <div style={styles.header}>{renderHeader(activeImage, activeIndex)}</div>
    );
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: Colors.role.canvas,
        outline: 'none',
      }}
      data-testid={testID}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {showHeader &&
        (renderHeader ? (
          header()
        ) : (
          <ImageGalleryHeader
            title={activeImage?.name}
            onBack={onBack}
            testID={testID ? `${testID}-header` : undefined}
          />
        ))}

      <View style={styles.viewerContainer}>
        {/* Main image area */}
        <div
          style={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onDoubleClick={handleDoubleClick}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {activeImage && (
            <img
              src={activeImage.url}
              alt={activeImage.name || 'Gallery image'}
              style={imageStyle}
              draggable={false}
            />
          )}
        </div>

        {/* Navigation arrows */}
        {!disableImageChange && images.length > 1 && scale <= 1 && (
          <>
            {activeIndex > 0 && (
              <Pressable
                style={[styles.navButton, styles.navButtonLeft]}
                onPress={handlePrev}
                accessibilityLabel="Previous image"
              >
                <ChevronLeftIcon size={24} color={colors.brand.white} />
              </Pressable>
            )}
            {activeIndex < images.length - 1 && (
              <Pressable
                style={[styles.navButton, styles.navButtonRight]}
                onPress={handleNext}
                accessibilityLabel="Next image"
              >
                <ChevronRightIcon size={24} color={colors.brand.white} />
              </Pressable>
            )}
          </>
        )}

        {/* Counter pill */}
        {showCounter && images.length > 1 && (
          <View style={styles.counterContainer} pointerEvents="none">
            <View style={styles.counterPill}>
              <Text style={styles.counterText}>
                {activeIndex + 1} / {images.length}
              </Text>
            </View>
          </View>
        )}

        {/* Zoom indicator */}
        {scale > 1 && (
          <View style={styles.zoomIndicator} pointerEvents="none">
            <Text style={styles.zoomText}>{Math.round(scale * 100)}%</Text>
          </View>
        )}
      </View>

      {hasThumbnails && (
        <ImageGalleryThumbnails
          images={thumbnailData}
          activeIndex={activeIndex}
          onThumbnailPress={goToIndex}
          testID={testID ? `${testID}-thumbnails` : undefined}
        />
      )}
    </div>
  );
};

ImageGallery.displayName = 'ImageGallery';

const styles = themedStyleSheet((role) => ({
  root: {
    flex: 1,
    backgroundColor: role.surfaceInverse,
  },
  viewerContainer: {
    flex: 1,
    position: 'relative',
  },
  navButton: {
    position: 'absolute',
    top: '50%',
    marginTop: -20,
    width: 40,
    height: 40,
    borderRadius: borderRadius.full,
    backgroundColor: 'rgba(2, 2, 2, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  navButtonLeft: {
    left: spacing.lg,
  },
  navButtonRight: {
    right: spacing.lg,
  },
  counterContainer: {
    position: 'absolute',
    bottom: spacing.lg,
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  counterPill: {
    backgroundColor: 'rgba(2, 2, 2, 0.6)',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.pill,
  },
  counterText: {
    ...typography.label.mediumEmphasised,
    color: colors.brand.white,
  },
  zoomIndicator: {
    position: 'absolute',
    top: spacing.lg,
    right: spacing.lg,
    backgroundColor: 'rgba(2, 2, 2, 0.6)',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.sm,
  },
  zoomText: {
    ...typography.label.mediumEmphasised,
    color: colors.brand.white,
  },
  header: { paddingTop: 80 },
}));
